<script setup lang="ts">
import { Item } from '@/@types/cart'
import { Order } from '@/@types/order'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table-custom'
import { formatPrice } from '@/helpers/current-price'
import { formatDateFull } from '@/helpers/format-date'
import { Link } from '@inertiajs/vue3'
import { useUrlSearchParams } from '@vueuse/core'
import { ArrowDown, ArrowUp, Eye } from 'lucide-vue-next'
import { computed, inject } from 'vue'

withDefaults(
    defineProps<{
        orders: Order[]
        hasSort?: boolean
        isAdmin?: boolean
        onlyProps?: string[]
    }>(),
    {
        isAdmin: false,
        onlyProps: () => ['orders', 'pagination'],
    },
)
const calculateTotalQuantity = (order: Order) => {
    return order.items.reduce((acc: number, item: Item) => acc + item.effective_quantity, 0)
}

const admin = inject<boolean | undefined>('admin')
const workshop_id = inject<number>('workshop_id')

const { sort, dateSort, ...params } = useUrlSearchParams()

const sortRef = computed(() => {
    return sort === 'asc' ? 'desc' : 'asc'
})

const dateSortRef = computed(() => {
    return dateSort === 'asc' ? 'desc' : 'asc'
})
</script>

<template>
    <Table class="">
        <TableHeader>
            <TableRow>
                <TableHead>
                    <div class="flex items-center gap-2">
                        {{ $t('Order number') }}
                        <Link
                            :href="isAdmin ? `/admin/orders` : `/orders`"
                            preserve-scroll
                            :only="onlyProps"
                            :data="{ ...params, sort: sortRef }"
                        >
                            <ArrowDown class="h-4 w-4 text-slate-500" v-if="sortRef === 'desc'" />
                            <ArrowUp class="h-4 w-4 text-slate-500" v-else-if="sortRef === 'asc'" />
                        </Link>
                    </div>
                </TableHead>
                <TableHead>E-mail comprador</TableHead>
                <TableHead>Grupo de ingresso</TableHead>
                <TableHead>{{ $t('Number of tickets') }}</TableHead>
                <TableHead>{{ $t('Total value') }}</TableHead>
                <TableHead>{{ $t('Status') }}</TableHead>
                <TableHead>
                    <div class="flex items-center gap-2">
                        Data
                        <Link
                            :href="isAdmin ? `/admin/orders` : `/orders`"
                            preserve-scroll
                            :only="onlyProps"
                            :data="{ ...params, dateSort: dateSortRef }"
                        >
                            <ArrowDown class="h-4 w-4 text-slate-500" v-if="dateSortRef === 'desc'" />
                            <ArrowUp class="h-4 w-4 text-slate-500" v-else-if="dateSortRef === 'asc'" />
                        </Link>
                    </div>
                </TableHead>

                <TableHead class="w-[90px]">{{ $t('Actions') }}</TableHead>
            </TableRow>
        </TableHeader>
        <TableBody>
            <TableRow v-for="order in orders" :key="order.id">
                <TableCell class="text-slate-800">{{ order.id }}</TableCell>
                <TableCell class="text-slate-800">{{ order.user.email }}</TableCell>
                <TableCell>{{ $t(`groups.${order.group}`) }}</TableCell>
                <TableCell>{{ calculateTotalQuantity(order) }}</TableCell>
                <TableCell>{{ formatPrice((order.transaction_detail?.amount || order.order_snap_shot.total_cents) / 100) }}</TableCell>
                <TableCell>
                    <Badge :variant="order.status">
                        {{ $t(order.status) }}
                    </Badge>
                </TableCell>
                <TableCell>
                    {{ formatDateFull(order.created_at) }}
                </TableCell>

                <TableCell>
                    <div class="flex items-center justify-center gap-3">
                        <Button
                            size="icon"
                            :as="Link"
                            :href="`${admin ? '/admin' : ''}/orders/${order.id}${admin ? `?workshop_id=${workshop_id}` : ''}`"
                            variant="ghost"
                            :aria-label="$t('View order')"
                        >
                            <Eye />
                        </Button>
                    </div>
                </TableCell>
            </TableRow>
        </TableBody>
    </Table>
</template>
